import { useState, useEffect } from 'react';
import { Clock, CheckCircle, XCircle, Mail, Users, Calendar, AlertCircle, Loader2, FileText, Eye, Send, UserPlus } from 'lucide-react';

import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Alert, AlertDescription } from '../ui/alert';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { useAdminAuth } from '../../hooks/useAuth';

import { PageTitle } from './shared';
import { useRememberedTab } from './navigation/useRememberedTab';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const REGISTRATION_TABS = ['pending_verification', 'pending_approval', 'approved', 'rejected', 'all'];

const RegistrationApprovalManager = () => {
  const { token } = useAdminAuth();

  const [registrations, setRegistrations] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useRememberedTab(
    'registrations.status-tab', REGISTRATION_TABS, 'pending_approval',
  );

  // Detail modal
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // Rejection dialog
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [processingAction, setProcessingAction] = useState(false);

  // Load registrations and stats
  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Load registrations for active tab
      const registrationsResponse = await fetch(
        `${BACKEND_URL}/api/admin/member-registrations?status=${activeTab === 'all' ? '' : activeTab}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (!registrationsResponse.ok) {
        throw new Error('Failed to load registrations');
      }

      const registrationsData = await registrationsResponse.json();
      setRegistrations(registrationsData);

      // Load stats
      const statsResponse = await fetch(
        `${BACKEND_URL}/api/admin/member-registrations/stats`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        setStats(statsData);
      }

    } catch (err) {
      console.error('Failed to load registrations:', err);
      setError('Failed to load registrations. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (registration) => {
    setSelectedRegistration(registration);
    setShowDetailModal(true);
  };

  const handleApprove = async (registration) => {
    if (!window.confirm(`Approve registration for ${registration.parent_name}? This will create customer and student accounts.`)) {
      return;
    }

    setProcessingAction(true);

    try {
      const response = await fetch(
        `${BACKEND_URL}/api/admin/member-registrations/${registration.id}/approve`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      const data = await response.json();

      if (!response.ok) {
        alert(data.detail || 'Failed to approve registration');
        return;
      }

      alert(`Registration approved! Customer ID: ${data.customer_id}, Student IDs: ${data.student_ids.join(', ')}`);
      loadData(); // Reload data
      setShowDetailModal(false);

    } catch (err) {
      console.error('Approval error:', err);
      alert('An error occurred while approving the registration');
    } finally {
      setProcessingAction(false);
    }
  };

  const handleReject = (registration) => {
    setSelectedRegistration(registration);
    setRejectionReason('');
    setShowRejectDialog(true);
  };

  const submitRejection = async () => {
    if (!rejectionReason.trim()) {
      alert('Please provide a rejection reason to send to the applicant');
      return;
    }

    setProcessingAction(true);

    try {
      const response = await fetch(
        `${BACKEND_URL}/api/admin/member-registrations/${selectedRegistration.id}/reject`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ rejection_reason: rejectionReason }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        alert(data.detail || 'Failed to reject registration');
        return;
      }

      alert('Registration rejected and notification sent to applicant');
      setShowRejectDialog(false);
      setShowDetailModal(false);
      loadData(); // Reload data

    } catch (err) {
      console.error('Rejection error:', err);
      alert('An error occurred while rejecting the registration');
    } finally {
      setProcessingAction(false);
    }
  };

  const handleResendVerification = async (registration) => {
    if (!window.confirm(`Resend verification email to ${registration.parent_email}?`)) {
      return;
    }

    setProcessingAction(true);

    try {
      const response = await fetch(
        `${BACKEND_URL}/api/admin/member-registrations/${registration.id}/resend-verification`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
        }
      );

      const data = await response.json();

      if (!response.ok) {
        alert(data.detail || 'Failed to resend verification email');
        return;
      }

      alert('Verification email sent successfully');

    } catch (err) {
      console.error('Resend error:', err);
      alert('An error occurred while sending the verification email');
    } finally {
      setProcessingAction(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (status) => {
    const variants = {
      pending_verification: { color: 'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300', label: 'Pending Verification' },
      pending_approval: { color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300', label: 'Pending Approval' },
      approved: { color: 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300', label: 'Approved' },
      rejected: { color: 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300', label: 'Rejected' },
    };
    const variant = variants[status] || variants.pending_verification;
    return <Badge className={variant.color}>{variant.label}</Badge>;
  };

  return (
    <div className="space-y-6">
      <PageTitle
        title="Member Registrations"
        description="Review and approve new member applications"
        icon={<UserPlus className="h-6 w-6" />}
      />

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Tabs for filtering */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="pending_verification">
            <Clock className="w-4 h-4 mr-1" />
            Verification
            {stats.pending_verification > 0 && (
              <Badge className="ml-2 bg-amber-500">{stats.pending_verification}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="pending_approval">
            <FileText className="w-4 h-4 mr-1" />
            Pending
            {stats.pending_approval > 0 && (
              <Badge className="ml-2 bg-blue-500">{stats.pending_approval}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="approved">
            <CheckCircle className="w-4 h-4 mr-1" />
            Approved
            {stats.approved > 0 && (
              <Badge className="ml-2 bg-gray-500">{stats.approved}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="rejected">
            <XCircle className="w-4 h-4 mr-1" />
            Rejected
            {stats.rejected > 0 && (
              <Badge className="ml-2 bg-gray-500">{stats.rejected}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="all">All</TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-6">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          ) : registrations.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-gray-500 dark:text-gray-400">
                No registrations found in this category
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {registrations.map((registration) => (
                <Card key={registration.id}>
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <h3 className="text-lg font-semibold">{registration.parent_name}</h3>
                          {getStatusBadge(registration.status)}
                        </div>

                        <div className="grid grid-cols-2 gap-4 text-sm text-gray-600 dark:text-gray-400">
                          <div className="flex items-center space-x-2">
                            <Mail className="w-4 h-4" />
                            <span>{registration.parent_email}</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Users className="w-4 h-4" />
                            <span>{registration.students?.length || 0} student(s)</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Calendar className="w-4 h-4" />
                            <span>{formatDate(registration.created_at)}</span>
                          </div>
                          {registration.email_verified && (
                            <div className="flex items-center space-x-2 text-green-600">
                              <CheckCircle className="w-4 h-4" />
                              <span>Email verified</span>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center space-x-2 ml-4">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleViewDetails(registration)}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </Button>

                        {registration.status === 'pending_verification' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleResendVerification(registration)}
                            disabled={processingAction}
                          >
                            <Send className="w-4 h-4 mr-1" />
                            Resend
                          </Button>
                        )}

                        {registration.status === 'pending_approval' && (
                          <>
                            <Button
                              size="sm"
                              onClick={() => handleApprove(registration)}
                              disabled={processingAction}
                              className="bg-green-600 hover:bg-green-700"
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Approve
                            </Button>
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => handleReject(registration)}
                              disabled={processingAction}
                            >
                              <XCircle className="w-4 h-4 mr-1" />
                              Reject
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Registration Details</DialogTitle>
            <DialogDescription>
              Review the full application details
            </DialogDescription>
          </DialogHeader>

          {selectedRegistration && (
            <div className="space-y-6">
              {/* Status */}
              <div>
                <Label className="text-sm font-medium">Status</Label>
                <div className="mt-1">{getStatusBadge(selectedRegistration.status)}</div>
              </div>

              {/* Parent Info */}
              <div className="border-t pt-4">
                <h3 className="font-semibold mb-3">Parent/Guardian Information</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <Label className="text-xs text-gray-500 dark:text-gray-400">Name</Label>
                    <p>{selectedRegistration.parent_name}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500 dark:text-gray-400">Email</Label>
                    <p>{selectedRegistration.parent_email}</p>
                  </div>
                  {selectedRegistration.parent_phone && (
                    <div>
                      <Label className="text-xs text-gray-500 dark:text-gray-400">Phone</Label>
                      <p>{selectedRegistration.parent_phone}</p>
                    </div>
                  )}
                  {selectedRegistration.parent_address && (
                    <div className="col-span-2">
                      <Label className="text-xs text-gray-500 dark:text-gray-400">Address</Label>
                      <p>{selectedRegistration.parent_address}</p>
                    </div>
                  )}
                  {selectedRegistration.emergency_contact_name && (
                    <>
                      <div>
                        <Label className="text-xs text-gray-500 dark:text-gray-400">Emergency Contact</Label>
                        <p>{selectedRegistration.emergency_contact_name}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500 dark:text-gray-400">Emergency Phone</Label>
                        <p>{selectedRegistration.emergency_contact_phone}</p>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Students */}
              <div className="border-t pt-4">
                <h3 className="font-semibold mb-3">Students ({selectedRegistration.students?.length || 0})</h3>
                <div className="space-y-3">
                  {selectedRegistration.students?.map((student, idx) => (
                    <Card key={idx} className="border-2">
                      <CardContent className="p-4">
                        <p className="font-medium mb-2">{student.name}</p>
                        <div className="grid grid-cols-2 gap-2 text-xs text-gray-600 dark:text-gray-400">
                          {student.date_of_birth && <p>DOB: {student.date_of_birth}</p>}
                          {student.age && <p>Age: {student.age}</p>}
                          <p>Belt: {student.current_belt}</p>
                          <p>Programs: {student.program_ids?.length || 0}</p>
                          {student.username && <p className="col-span-2">Username: {student.username}</p>}
                          {student.email && <p className="col-span-2">Email: {student.email}</p>}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>

              {/* Waivers */}
              {selectedRegistration.waiver && (
                <div className="border-t pt-4">
                  <h3 className="font-semibold mb-3">Waivers & Consent</h3>
                  <div className="text-sm space-y-2">
                    <p className="flex items-center">
                      <CheckCircle className="w-4 h-4 mr-2 text-green-600" />
                      Liability waiver accepted
                    </p>
                    <p className="flex items-center">
                      <CheckCircle className="w-4 h-4 mr-2 text-green-600" />
                      Photo/Video: {selectedRegistration.waiver.photo_video_consent === 'consent' ? 'Consent' : 'Do Not Consent'}
                    </p>
                    {selectedRegistration.waiver.photo_video_caption_preference && (
                      <p className="text-gray-600 dark:text-gray-400">Caption preference: {selectedRegistration.waiver.photo_video_caption_preference}</p>
                    )}
                    <p className="text-gray-600 dark:text-gray-400">
                      Signed by: {selectedRegistration.waiver.digital_signature} on {formatDate(selectedRegistration.waiver.liability_waiver_accepted_at)}
                    </p>
                  </div>
                </div>
              )}

              {/* Timestamps */}
              <div className="border-t pt-4">
                <h3 className="font-semibold mb-3">Timeline</h3>
                <div className="text-sm space-y-1">
                  <p>Submitted: {formatDate(selectedRegistration.created_at)}</p>
                  {selectedRegistration.email_verified_at && (
                    <p>Email verified: {formatDate(selectedRegistration.email_verified_at)}</p>
                  )}
                  {selectedRegistration.approved_at && (
                    <p>Approved: {formatDate(selectedRegistration.approved_at)} by {selectedRegistration.approved_by_admin}</p>
                  )}
                  {selectedRegistration.rejected_at && (
                    <p>Rejected: {formatDate(selectedRegistration.rejected_at)} by {selectedRegistration.rejected_by_admin}</p>
                  )}
                </div>
              </div>

              {/* Rejection reason */}
              {selectedRegistration.rejection_reason && (
                <div className="border-t pt-4">
                  <h3 className="font-semibold mb-3">Rejection Reason</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{selectedRegistration.rejection_reason}</p>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            {selectedRegistration?.status === 'pending_approval' && (
              <>
                <Button
                  variant="destructive"
                  onClick={() => {
                    setShowDetailModal(false);
                    handleReject(selectedRegistration);
                  }}
                  disabled={processingAction}
                >
                  <XCircle className="w-4 h-4 mr-1" />
                  Reject
                </Button>
                <Button
                  onClick={() => handleApprove(selectedRegistration)}
                  disabled={processingAction}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {processingAction ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Approve Registration
                    </>
                  )}
                </Button>
              </>
            )}
            <Button variant="outline" onClick={() => setShowDetailModal(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rejection Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Registration</DialogTitle>
            <DialogDescription>
              Provide a message that will be sent to the applicant explaining why their registration was not approved.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="rejection-reason">Rejection Message</Label>
              <Textarea
                id="rejection-reason"
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="e.g., We're sorry, but we're currently at full capacity for the Kids Karate program. We've added you to our waitlist and will contact you when a spot opens up."
                rows={5}
                className="mt-2"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                This message will be included in the rejection email sent to {selectedRegistration?.parent_email}
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRejectDialog(false)} disabled={processingAction}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={submitRejection}
              disabled={processingAction || !rejectionReason.trim()}
            >
              {processingAction ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <XCircle className="w-4 h-4 mr-1" />
                  Send Rejection
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RegistrationApprovalManager;
